import { Chip, SquareButton } from 'components';
import { Participation, ParticipationTag, Session } from '@app/api';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import classNames from 'classnames';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  tagContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase,
    width: theme.marginBase * 35,
    maxHeight: theme.marginBase * 8,
    transition: 'width 0.3s ease',
    overflow: 'hidden',
  },
  button: {
    minHeight: theme.marginBase * 3,
    minWidth: theme.marginBase * 3,
    maxHeight: theme.marginBase * 3,
    maxWidth: theme.marginBase * 3,
  },
  icon: {
    fontSize: theme.iconSize.small,
  },
  expanded: {
    width: theme.marginBase * 50,
  },
}));

interface Props {
  participation: Participation;
  session: Session;
}

const getTagDetail = (
  tagId: string,
  session: Session,
): ParticipationTag | undefined => {
  return session.availableTags?.find((tag) => tag.id === tagId);
};

export const TableParticipationTagList = ({
  participation,
  session,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);

  const tags = useMemo(() => participation.tagsIds ?? [], [participation]);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(tags.length);

  useLayoutEffect(() => {
    const el = ref.current;
    const maxHeight = theme.marginBase * 8;

    if (!el || tags.length === 0 || expanded) {
      return;
    }

    if (el.scrollHeight <= maxHeight) {
      setVisibleCount(tags.length);
      return;
    }

    const children = Array.from(el.children);
    let count = 0;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      const rect = child.getBoundingClientRect();
      const containerRect = el.getBoundingClientRect();

      if (rect.bottom <= containerRect.top + maxHeight) {
        count = i + 1;
      } else {
        break;
      }
    }

    setVisibleCount(Math.max(1, count - 1));
  }, [tags, expanded, theme.marginBase]);

  if (tags.length === 0) return null;

  const tagsToShow = expanded ? tags : tags.slice(0, visibleCount);
  const hasMore = tagsToShow.length < tags.length;

  return (
    <div
      ref={ref}
      className={classNames(classes.tagContainer, {
        [classes.expanded]: expanded,
      })}
    >
      {tagsToShow.map((tagId) => {
        const tag = getTagDetail(tagId, session);
        return (
          <Chip key={tagId} color={tag?.color}>
            {tag?.name}
          </Chip>
        );
      })}

      {hasMore && !expanded && (
        <SquareButton
          className={classes.button}
          small
          color="ink"
          icon={classnames('ri-more-line', classes.icon)}
          onClick={() => setExpanded(true)}
          data-no-row-click
        />
      )}
    </div>
  );
};
